/**
 * In-app preview of an `.xlsx` mail attachment — most concretely, the RFQ workbook the
 * backend already generates (`docs/bosun-frappe-open-questions.md` Q12: real evidence pulled
 * from a live `Communication`, `RFQ_2026-05-07_AMS-RFQ-7603fa3ef4.xlsx`). Fetches the file
 * from its `preview_url` and parses EVERY sheet into a spreadsheet-shaped grid — no cell
 * styling, no formulas evaluated, just what a purchaser needs to read the workbook without
 * downloading it, laid out the way a spreadsheet actually looks (column letters, row numbers,
 * a frozen header, right-aligned numbers).
 *
 * ---- Parser choice ----
 *
 * `read-excel-file` (https://www.npmjs.com/package/read-excel-file), the `/universal` build
 * specifically:
 *   - Actively maintained (published days before this was written), TypeScript-native types,
 *     zero native/binary dependencies (`fflate` for the zip container, `saxen` for the XML —
 *     both pure JS).
 *   - The alternatives are worse fits: `xlsx` (SheetJS)'s free npm build is a frozen release
 *     with a known prototype-pollution history and ships as one ~700kB-minified blob with no
 *     granular entry points to lazy-load a slice of; `exceljs` is Node-oriented (streams,
 *     `fs`), ~1MB+ and awkward to bundle for a browser at all.
 *   - The `/universal` entry point (vs. `/browser`) does its work on the calling thread
 *     instead of spawning a Web Worker internally. For an ~8KB workbook that's the right
 *     trade: no worker-bundling complexity, and it behaves identically under Vite and under
 *     Vitest/jsdom (which has no reliable `Worker`), so this module is unit-testable without
 *     mocking browser APIs.
 *   - Verified against the REAL attachment, not assumed: the workbook has no
 *     `xl/sharedStrings.xml` at all — every cell is `t="inlineStr"` with the text inline
 *     (`<is><t>…</t></is>`). `read-excel-file` reads it correctly with no special handling;
 *     see `xlsxPreview.test.ts`, which fixtures the real cell XML this file's rows came from.
 *
 * This module is imported ONLY via a dynamic `import()` (below), never a static one — the
 * xlsx preview is a rarely-opened view and the parser must not sit in the app's main chunk.
 *
 * The default export, `readXlsxFile`, reads every sheet (`{ sheet, data }[]`) rather than
 * `readSheet`'s single-sheet read — a real workbook the RFQ template doesn't dictate (a
 * customer's own item list, a supplier's reply) can have more than one tab, and a preview
 * that silently shows only the first would misrepresent the file.
 *
 * ---- Shape of a real workbook, and how it's split ----
 *
 * The real RFQ workbook is not a plain grid: rows 4–13 are a sparse label/value header block
 * (company name, `ENQUIRY NO:` / `RFQ ID:` / `CURRENCY:` …, scattered across columns A and G),
 * then one fully-populated row of column titles (`SR NO | IMPA CODE | … | Opportunity Item`),
 * then the line rows. `buildSheetPreview` below finds that title row by density — the first
 * row where most cells are non-empty — rather than hunting for "SR NO" by name, so this also
 * does something reasonable for a workbook that isn't the RFQ template. Each sheet in the
 * workbook is split independently, since a second sheet need not share the first's layout.
 *
 * ---- Embedded images ----
 *
 * A customer's own requisition workbook (as opposed to the backend-generated RFQ template above)
 * routinely carries photos — a nameplate, a part, a damaged fitting — as OOXML drawing objects,
 * invisible to `read-excel-file` because they sit outside any cell. `xlsxImages.ts` extracts
 * those independently (same fetched bytes, a separate zip walk) and this module folds the result
 * in here: each image is attached to the `XlsxSheetPreview` its drawing resolves to, and — this
 * is the one filtering change images make to the sheet list below — a sheet is kept even with
 * NO cell data at all when it holds images, rather than being dropped as "empty". That's not a
 * hypothetical: both real requisition workbooks this was verified against (see
 * `xlsxImages.ts`'s header) put every photo on a dedicated `photos-docs` sheet with zero cells,
 * which the pre-existing "empty sheet" filter would otherwise have silently eaten. Images whose
 * sheet couldn't be resolved land in `XlsxPreview.otherImages` instead of being dropped.
 *
 * ---- Merged cells, column widths, and trimmed dead space ----
 *
 * A real customer requisition workbook leans on merged cells for its header block far more than
 * the backend-generated RFQ template above does, and on authored column widths throughout —
 * `xlsxLayout.ts`'s header documents the actual live-bench file (`FORM 19-069 BEDSHEET &
 * PILLOW.xlsx`) whose 72 merges, spread across a sheet with no single "dense" title row, is what a
 * purchaser's "lots of space and indentation error" complaint about this preview turned out to
 * be: one populated cell followed by several blank grid cells where Excel shows one wide cell,
 * repeated down the sheet. `buildSheetPreview` below now takes that module's `XlsxSheetLayout`
 * (optional — a sheet it couldn't resolve, or a caller with no layout data at all, still gets a
 * plain grid) and: renders a merge as one `colSpan`/`rowSpan` cell rather than an anchor plus N
 * blanks (`buildMergedGrid`), sizes columns from their authored width rather than raw
 * browser auto-layout (`buildColumnWidthsPx`), and trims fully-blank LEADING/TRAILING rows and
 * columns as the layout padding they are — never an interior blank, which is a deliberate
 * separator, not padding (`trimBlankEdges`). Column letters stay true to the untrimmed source
 * throughout (`XlsxSheetTable.colOffset`) — trimming a blank margin column must never make the
 * chrome lie about which real column a value came from.
 */

import type { CellValue, Row, Sheet } from 'read-excel-file/universal'
import { extractXlsxImages, type XlsxImage } from './xlsxImages'
import { isLegacyXls } from './legacyXls'
import { downloadAttachmentBlob } from './api/attachmentBlob'
import { charWidthToPx, extractXlsxLayouts, type XlsxMerge, type XlsxSheetLayout } from './xlsxLayout'
export type { XlsxImage } from './xlsxImages'

/** One data cell, typed enough to know how to align it — numbers right, everything else left —
 * plus, since `xlsxLayout.ts` gave this module merge-cell data to work with, enough to describe
 * its place in a merged region. */
export interface XlsxCell {
  text: string
  numeric: boolean
  /** >1 when this cell ANCHORS a merged region reaching right — render as `<td colSpan>`. Absent
   * (treated as 1) for an ordinary cell. */
  colSpan?: number
  /** >1 when this cell anchors a merged region reaching down — render as `<td rowSpan>`. Absent
   * (treated as 1) for an ordinary cell. */
  rowSpan?: number
  /** True when this position is INSIDE a merged region but is not its anchor. The renderer must
   * skip it entirely — no `<td>` at all, not even an empty one — or the anchor's colSpan/rowSpan
   * would double-count the column/row it already covers. Real OOXML never stores a value in a
   * covered cell (only the anchor holds one), so `text`/`numeric` here are always blank/false;
   * `covered` is what the renderer actually keys off. */
  covered?: boolean
}

/** The tabular part of one sheet — everything `buildSheetPreview` can produce from cell data
 * alone. Kept as its own type because `buildSheetPreview` has no access to embedded images (a
 * completely separate data source, the zip's drawing relationships, not sheet rows) — the
 * caller attaches `images` afterwards to make the full `XlsxSheetPreview`. */
export type XlsxSheetTable = {
  name: string
  /** Free-text lines above the data table (company/enquiry/RFQ header block), one per row. */
  headerLines: string[]
  /** Column titles, from the first "dense" row. Empty when no such row was found. */
  columns: string[]
  /** Data rows below the column-title row, capped at `MAX_PREVIEW_ROWS` — see `truncated`. */
  rows: XlsxCell[][]
  /** How many data rows this sheet actually has, before any capping. */
  rowCount: number
  /** The grid's width — `columns.length`, widened if some data row runs longer. */
  columnCount: number
  /** True when `rows.length < rowCount` — some data rows exist but are not shown. */
  truncated: boolean
  /** How many of the ORIGINAL sheet's leading columns were trimmed as dead space (see
   * `trimBlankEdges`) — added to a rendered column index before `columnLetter()` so the chrome
   * always shows the sheet's TRUE column letters, never renumbered from `A` just because this
   * preview trimmed a blank margin the source workbook also treats as padding. */
  colOffset: number
  /** Authored column width in CSS pixels (`charWidthToPx`, from `xlsxLayout.ts`'s
   * `<cols customWidth="1">` reading), one entry per rendered column in the same order as
   * `columnCount` — `undefined` where the sheet author never widened/narrowed that column, left
   * to the browser's ordinary auto-layout. */
  columnWidthsPx: (number | undefined)[]
}

export interface XlsxSheetPreview extends XlsxSheetTable {
  /** Embedded images whose drawing was resolved to this sheet (see this module's header and
   * `xlsxImages.ts`). Always present, possibly empty — never undefined. */
  images: XlsxImage[]
}

export interface XlsxPreview {
  sheets: XlsxSheetPreview[]
  /** Images found in the workbook whose owning sheet this parser could not resolve — still
   * shown, just not filed under a sheet tab. Expect this to be empty for the vast majority of
   * real workbooks. */
  otherImages: XlsxImage[]
  /** Set when the embedded-images scan itself failed (couldn't even list `xl/media/*`) — distinct
   * from "no images found". The UI must say so explicitly rather than silently showing none. */
  imageScanError: string | null
}

export type XlsxPreviewFailureReason = 'fetch_failed' | 'not_xlsx' | 'empty' | 'parse_failed'

export interface XlsxPreviewFailure {
  ok: false
  reason: XlsxPreviewFailureReason
  detail: string
}

export interface XlsxPreviewSuccess {
  ok: true
  preview: XlsxPreview
}

export type XlsxPreviewResult = XlsxPreviewSuccess | XlsxPreviewFailure

/** A hard cap on rendered data rows per sheet. Not a network/parse limit — the whole sheet is
 * still parsed (so `rowCount` is exact) — purely so a 50,000-row export doesn't hand the DOM
 * that many `<tr>`s. `truncated` on the returned sheet says when this actually bit. */
export const MAX_PREVIEW_ROWS = 500

function cellText(value: CellValue | null): string {
  if (value === null || value === undefined) return ''
  if (value instanceof Date) return value.toISOString().slice(0, 10)
  if (typeof value === 'boolean') return value ? 'TRUE' : 'FALSE'
  return String(value)
}

function toXlsxCell(value: CellValue | null): XlsxCell {
  return { text: cellText(value), numeric: typeof value === 'number' }
}

const isBlankRow = (row: Row): boolean => row.every((cell) => cell === null || cell === '')

/**
 * A "column title" row: most of its cells are filled, unlike the sparse label/value rows in
 * a header block (at most 3-4 of 10 cells in the real workbook). `0.7` clears that gap
 * without demanding every last cell — a title row with one legitimately blank column heading
 * should still count.
 */
function isDenseRow(row: Row, minFilledFraction = 0.7): boolean {
  if (row.length === 0) return false
  const filled = row.filter((cell) => cell !== null && cell !== '').length
  return filled / row.length >= minFilledFraction
}

/** Zero-based column index → spreadsheet letters (`0` → `A`, `25` → `Z`, `26` → `AA`, …). Pure,
 * used by the grid to draw its own column-letter chrome row. Callers rendering a TRIMMED sheet
 * must add `colOffset` before calling this — see `XlsxSheetTable.colOffset`. */
export function columnLetter(index: number): string {
  let n = index + 1
  let letters = ''
  while (n > 0) {
    const rem = (n - 1) % 26
    letters = String.fromCharCode(65 + rem) + letters
    n = Math.floor((n - 1) / 26)
  }
  return letters
}

/** `rows[*][col]` blank in EVERY row — a fully empty column across the whole (row-trimmed) sheet,
 * as opposed to a column that's merely blank on some rows (an item list's unused "Remarks" column
 * on most lines, say) — only the former is layout padding, never the latter. `c >= row.length` —
 * a row that simply doesn't reach this far right — counts as blank at that position too, matching
 * how the renderer already treats a missing array slot. */
function isBlankColumn(rows: Row[], col: number): boolean {
  return rows.every((row) => {
    const cell = col < row.length ? row[col] : null
    return cell === null || cell === undefined || cell === ''
  })
}

/** Leading/trailing fully-blank rows AND columns are layout padding in the source file, not data
 * — a thin spacer row (a coloured bar, no cell values) above a real header block, an unused margin
 * column either side of the real grid. Trims both from the EDGES only; an interior blank row/column
 * (a deliberate separator between two item groups, an unused-on-every-line-but-still-real column
 * in the middle of the table) is left exactly alone — see this module's header for the real
 * workbook (`FORM 19-069 BEDSHEET & PILLOW.xlsx`) whose row 1 is exactly this: a `ht="4.5"` spacer
 * with no values, rendered as a visible empty grid row before this existed. Returns `rowOffset`/
 * `colOffset` (how many leading rows/columns were cut) so merge coordinates — always expressed in
 * the ORIGINAL sheet's own indices — can be translated into this trimmed space. */
function trimBlankEdges(rows: Row[]): { rows: Row[]; rowOffset: number; colOffset: number; colCount: number } {
  let rowStart = 0
  let rowEnd = rows.length
  while (rowStart < rowEnd && isBlankRow(rows[rowStart])) rowStart++
  while (rowEnd > rowStart && isBlankRow(rows[rowEnd - 1])) rowEnd--
  const rowTrimmed = rows.slice(rowStart, rowEnd)

  if (rowTrimmed.length === 0) return { rows: [], rowOffset: rowStart, colOffset: 0, colCount: 0 }

  const width = rowTrimmed.reduce((max, row) => Math.max(max, row.length), 0)
  let colStart = 0
  let colEnd = width
  while (colStart < colEnd && isBlankColumn(rowTrimmed, colStart)) colStart++
  while (colEnd > colStart && isBlankColumn(rowTrimmed, colEnd - 1)) colEnd--

  const trimmed = rowTrimmed.map((row) => row.slice(colStart, colEnd))
  return { rows: trimmed, rowOffset: rowStart, colOffset: colStart, colCount: colEnd - colStart }
}

/**
 * Lays `dataRows` (already row- and column-trimmed, and already row-capped to `maxRows`) out as a
 * `columnCount`-wide grid of `XlsxCell`, then overlays `merges` on top: each merge's anchor cell
 * gets `colSpan`/`rowSpan`, every other cell it covers is replaced with a blank `covered: true`
 * cell the renderer must skip outright (see `XlsxCell.covered`'s doc for why).
 *
 * `merges` are in ORIGINAL sheet coordinates (straight from `xlsxLayout.ts` — that module knows
 * nothing about trimming or which rows ended up as `dataRows` vs. the header block). `dataRowOrigin`
 * is the original row index `dataRows[0]` came from, and `colOffset` the original column index
 * `dataRows[*][0]` came from — together they translate a merge into this grid's local coordinates.
 * A merge whose anchor lands before `dataRows[0]` (it's inside the flattened header block, or the
 * title row — both rendered as text, not a grid) is silently skipped, not an error: this is exactly
 * how a header block's own merges (verified real, see this module's header) are meant to be
 * handled — via `headerLines`' plain-text join, not colSpan. A merge that runs past the LAST
 * rendered row/column (truncated by `maxRows`, or by column-trimming) is clipped to fit rather
 * than dropped, so a merge that's mostly on-screen still renders as far as it can.
 */
function buildMergedGrid(dataRows: Row[], columnCount: number, merges: XlsxMerge[], dataRowOrigin: number, colOffset: number): XlsxCell[][] {
  const grid: XlsxCell[][] = dataRows.map((row) =>
    Array.from({ length: columnCount }, (_, c) => toXlsxCell(c < row.length ? row[c] : null)),
  )
  if (grid.length === 0 || columnCount === 0) return grid

  for (const merge of merges) {
    const localRow = merge.row - dataRowOrigin
    const localCol = merge.col - colOffset
    if (localRow < 0 || localRow >= grid.length || localCol >= columnCount) continue // anchor outside the rendered grid entirely

    const colStart = Math.max(0, localCol)
    const colEnd = Math.min(columnCount - 1, localCol + merge.colSpan - 1)
    const rowEnd = Math.min(grid.length - 1, localRow + merge.rowSpan - 1)
    if (colStart > colEnd || rowEnd < localRow) continue

    const anchor = grid[localRow][colStart]
    const colSpan = colEnd - colStart + 1
    const rowSpan = rowEnd - localRow + 1
    if (colSpan > 1) anchor.colSpan = colSpan
    if (rowSpan > 1) anchor.rowSpan = rowSpan

    for (let r = localRow; r <= rowEnd; r++) {
      for (let c = colStart; c <= colEnd; c++) {
        if (r === localRow && c === colStart) continue
        grid[r][c] = { text: '', numeric: false, covered: true }
      }
    }
  }

  return grid
}

/** `layout.columnWidths` (original sheet coordinates, only columns the author actually resized)
 * → one entry per RENDERED column, converted to a CSS pixel width, `undefined` where the sheet
 * never authored a width for that column. */
function buildColumnWidthsPx(columnCount: number, columnWidths: Map<number, number> | undefined, colOffset: number): (number | undefined)[] {
  return Array.from({ length: columnCount }, (_, c) => {
    const width = columnWidths?.get(c + colOffset)
    return width === undefined ? undefined : charWidthToPx(width)
  })
}

/** Raw sheet rows → header block + column titles + data rows, for one sheet. `null` for a
 * wholly blank sheet. Returns `XlsxSheetTable`, not the full `XlsxSheetPreview` — this function
 * only ever sees cell data (plus, now, the sheet's OWN merge/column-width metadata via `layout` —
 * still no embedded images, a completely separate data source); `fetchAndParseXlsxPreview`
 * attaches `images` afterwards. `layout` is optional so every existing caller that doesn't have
 * one (a sheet `xlsxLayout.ts` couldn't resolve, or a test exercising cell logic alone) still gets
 * a plain, merge-free grid rather than being forced to fabricate one. */
export function buildSheetPreview(name: string, rows: Row[], maxRows: number = MAX_PREVIEW_ROWS, layout?: XlsxSheetLayout): XlsxSheetTable | null {
  const { rows: trimmed, rowOffset, colOffset, colCount } = trimBlankEdges(rows)
  if (trimmed.length === 0) return null

  const headerRowIndex = trimmed.findIndex((row) => isDenseRow(row))
  const tableStart = headerRowIndex === -1 ? 0 : headerRowIndex

  const headerLines = trimmed
    .slice(0, tableStart)
    .map((row) =>
      row
        .map(cellText)
        .filter((cell) => cell !== '')
        .join('   '),
    )
    .filter((line) => line !== '')

  const columns = headerRowIndex === -1 ? [] : trimmed[headerRowIndex].map(cellText)
  const dataRowStart = tableStart + (headerRowIndex === -1 ? 0 : 1)
  const dataRows = trimmed.slice(dataRowStart)

  const rowCount = dataRows.length
  const columnCount = Math.max(columns.length, colCount)
  const truncated = rowCount > maxRows
  const cappedDataRows = dataRows.slice(0, maxRows)

  const rowsOut = buildMergedGrid(cappedDataRows, columnCount, layout?.merges ?? [], rowOffset + dataRowStart, colOffset)
  const columnWidthsPx = buildColumnWidthsPx(columnCount, layout?.columnWidths, colOffset)

  return { name, headerLines, columns, rows: rowsOut, rowCount, columnCount, truncated, colOffset, columnWidthsPx }
}

const isEmptySheetTable = (table: XlsxSheetTable): boolean =>
  table.headerLines.length === 0 && table.columns.length === 0 && table.rows.length === 0

/** The shell for a sheet kept ONLY because it holds images — see this module's header. Same
 * shape `buildSheetPreview` would produce for a genuinely blank sheet, so `SheetGrid` renders
 * its existing "no tabular data" placeholder without needing to know why the sheet has no rows. */
const emptySheetTable = (name: string): XlsxSheetTable => ({
  name,
  headerLines: [],
  columns: [],
  rows: [],
  rowCount: 0,
  columnCount: 0,
  truncated: false,
  colOffset: 0,
  columnWidthsPx: [],
})

/**
 * Fetch `url` and parse every sheet. Every failure mode is a value, not a throw — the
 * caller always has something honest to show: a fetch that fails, a server error status, a
 * file that isn't a zip at all (mock/dev attachments are text stubs — see
 * `mock-server/uploads.ts`'s `sendStubFile` — so this case is exercised for real in offline
 * dev, not just theoretically), a workbook with no data on any sheet, or any other parse
 * failure.
 */
/** The uncached path, kept as the default so this module needs no query client of its own —
 * `downloadAttachmentBlob` because it carries the proxy bearer the deployed `/api` gate wants. */
const defaultLoadBlob = downloadAttachmentBlob

/**
 * What an unreadable spreadsheet actually needs the purchaser to do.
 *
 * The single message these replaced — "This file doesn't look like a valid .xlsx workbook" — was
 * true and useless. It reads as "your file is broken" when the file is usually fine, and says
 * nothing about which of four quite different things went wrong.
 *
 * `XLS_FILE_NOT_SUPPORTED` is now all but unreachable: `fetchAndParseXlsxPreview` detects the OLE2
 * magic number and routes legacy workbooks to `legacyXls.ts` before `read-excel-file` ever sees
 * them, so a real `.xls` is READ rather than refused. The case is kept for the one way it can
 * still fire — a file whose signature says something else entirely — and no longer tells anyone to
 * go and re-save it.
 */
export function describeInvalidInput(code: string): string {
  switch (code) {
    case 'XLS_FILE_NOT_SUPPORTED':
      return (
        'This looks like a legacy .xls workbook but does not carry the signature of one, so ' +
        'neither reader could open it. It may be damaged — ask the sender to resend it, ideally ' +
        'as .xlsx. You can download it below in the meantime.'
      )
    case 'FILE_NOT_SUPPORTED':
      return 'This attachment is not a spreadsheet, so there is nothing to lay out as one.'
    case 'INVALID_ZIP':
      return 'This .xlsx file is damaged — its container could not be opened. Ask the sender to resend it.'
    case 'NO_DATA':
      return 'This workbook is empty — it has no sheets with any data in them.'
    default:
      return "This file could not be read as a spreadsheet."
  }
}

export async function fetchAndParseXlsxPreview(
  url: string,
  /** Optional CACHED download (`api/attachmentBlob.ts`), so previewing a sheet and then reading
   * the mail with AI costs one request rather than two. Defaults to a plain authenticated
   * fetch — callers with no query client are unaffected. */
  loadBlob?: (url: string) => Promise<Blob>,
): Promise<XlsxPreviewResult> {
  let blob: Blob
  try {
    blob = loadBlob ? await loadBlob(url) : await defaultLoadBlob(url)
  } catch (error) {
    const detail = error instanceof Error && /\d{3}/.test(error.message)
      ? error.message
      : 'Could not reach the file — check your connection.'
    return { ok: false, reason: 'fetch_failed', detail }
  }

  /*
    LEGACY `.xls` FIRST, decided on the bytes rather than the filename.

    An OLE2 compound file is a different format, not a broken `.xlsx`, and `read-excel-file` refuses
    it by design. Real requisitions arrive in it, so it gets its own parser (`legacyXls.ts`) — also
    dynamically imported, so neither parser is in the bundle until a spreadsheet is opened and the
    7.5MB legacy one only when a legacy file actually turns up.

    Images and merge/column layout are `.xlsx` ZIP concerns and simply do not exist here, so this
    path returns the sheets and nothing else. The preview renders; it just renders plainly.
  */
  const headBytes = new Uint8Array(await blob.slice(0, 8).arrayBuffer())
  if (isLegacyXls(headBytes)) {
    try {
      const { parseLegacyXls } = await import('./legacyXls')
      const legacy = await parseLegacyXls(new Uint8Array(await blob.arrayBuffer()))
      const sheets = legacy
        // `LegacyCell[]` is `CellValue[]` by construction — see `legacyXls.ts`'s own type note.
        .map((sheet) => buildSheetPreview(sheet.name, sheet.rows as Row[]))
        .filter((sheet): sheet is XlsxSheetTable => sheet !== null)
        .map((sheet) => ({ ...sheet, images: [] as XlsxImage[] }))

      if (sheets.length === 0) {
        return { ok: false, reason: 'empty', detail: 'This workbook has no readable sheet.' }
      }
      // `null`, not a message: the images scan did not FAIL here, it does not apply — a `.xls`
      // has no `xl/media/*` to list. Reporting an error would have the UI apologise for the
      // absence of something that was never there.
      return { ok: true, preview: { sheets, otherImages: [], imageScanError: null } }
    } catch (error) {
      return {
        ok: false,
        reason: 'parse_failed',
        detail:
          error instanceof Error
            ? `This legacy .xls could not be read (${error.message}).`
            : 'This legacy .xls could not be read.',
      }
    }
  }

  // Dynamic import: this is the one place the parser enters the bundle graph, and only when
  // an xlsx preview is actually opened.
  const { default: readXlsxFile, InvalidInputError, InvalidSpreadsheetError } = await import('read-excel-file/universal')

  let workbook: Sheet[]
  try {
    workbook = await readXlsxFile(blob)
  } catch (error) {
    if (error instanceof InvalidInputError) {
      // The package's own typed code says WHICH of four things went wrong — see
      // `describeInvalidInput`. A legacy `.xls` is the common one and is fixable in one step.
      return { ok: false, reason: 'not_xlsx', detail: describeInvalidInput(error.code) }
    }
    if (error instanceof InvalidSpreadsheetError) {
      return { ok: false, reason: 'parse_failed', detail: 'This spreadsheet could not be read.' }
    }
    return {
      ok: false,
      reason: 'parse_failed',
      detail: error instanceof Error ? error.message : 'This spreadsheet could not be read.',
    }
  }

  // Independent of read-excel-file's own parse above — same bytes, two more separate zip walks:
  // one for the drawing objects it never looks at (`xlsxImages.ts`), one for the merge/
  // column-width layout metadata it also never looks at (`xlsxLayout.ts`). Both read this same
  // `ArrayBuffer` rather than each calling `blob.arrayBuffer()` again — a fresh copy either way,
  // but there's no reason to ask the `Blob` to make two when one covers both. See this module's
  // header and both of those modules' for why three independent unzips of the same bytes is the
  // right trade-off here.
  const bytes = new Uint8Array(await blob.arrayBuffer())
  const [imageExtraction, layoutExtraction] = await Promise.all([extractXlsxImages(bytes), extractXlsxLayouts(bytes)])

  const imagesBySheetName = new Map<string, XlsxImage[]>()
  const otherImages: XlsxImage[] = []
  if (imageExtraction.status === 'ok') {
    for (const image of imageExtraction.images) {
      if (image.sheetName === null) {
        otherImages.push(image)
        continue
      }
      const existing = imagesBySheetName.get(image.sheetName)
      if (existing) existing.push(image)
      else imagesBySheetName.set(image.sheetName, [image])
    }
  }
  const imageScanError = imageExtraction.status === 'scan_failed' ? imageExtraction.detail : null

  // Best-effort like the image scan above, but with no user-visible failure note: a failed
  // layout scan degrades to plain (merge-free, auto-width) rendering — still a correct, readable
  // preview of every cell's actual VALUE, just without the extra layout fidelity — never a lost
  // sheet or a blocked preview the way a failed image scan or a failed cell-value parse would be.
  const layoutsBySheetName = layoutExtraction.status === 'ok' ? layoutExtraction.layouts : new Map()

  const sheets = workbook
    .map((sheet): XlsxSheetPreview | null => {
      const table = buildSheetPreview(sheet.sheet, sheet.data, MAX_PREVIEW_ROWS, layoutsBySheetName.get(sheet.sheet))
      const images = imagesBySheetName.get(sheet.sheet) ?? []
      // A sheet with no cell data is normally dropped as "empty" — but not when it holds
      // images: both real requisition workbooks this was verified against put every photo on a
      // sheet exactly like that (see this module's header). `table` already isn't empty in the
      // ordinary case, so this only ever swaps in the shell for the images-only case.
      const resolvedTable = table && !isEmptySheetTable(table) ? table : images.length > 0 ? emptySheetTable(sheet.sheet) : null
      return resolvedTable ? { ...resolvedTable, images } : null
    })
    .filter((sheet): sheet is XlsxSheetPreview => sheet !== null)

  const hasAnyImages = imageExtraction.status === 'ok' && imageExtraction.images.length > 0
  if (sheets.length === 0 && !hasAnyImages) {
    return {
      ok: false,
      reason: 'empty',
      detail: imageScanError
        ? `This workbook has no data on any sheet, and its embedded images could not be scanned (${imageScanError}).`
        : 'This workbook has no data on any sheet.',
    }
  }

  return { ok: true, preview: { sheets, otherImages, imageScanError } }
}
